import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { activePanelState, completePanelState, PanelStatus, resetPanelState } from '@foradevelopment/fora-ui';
import {
  AttributablePay,
  PayReportingTableRowViewModel,
  PayReportingTableRowViewModelOld,
} from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import {
  deleteDrafts,
  getPayMonthOverviews,
  getPayReportData,
  getPayReportDataOld,
  reportPayroll,
  setEmploymentPayDraft,
} from './payrollThunks';
import { PayrollState } from './types/index';
import {
  ManualPayrollReportSummary,
  SetReviewAndConfirmCheckboxPayload,
  SetSelectedReportingPeriodPayload,
} from '../../payroll/types';

export const initialPayrollState: PayrollState = {
  inputPayData: {},
  payReportingTableData: [],
  payReportingTableDataOld: [],
  draftUpdatedAt: undefined,
  reviewAndConfirmChecked: false,
  selectedPayMonthOverview: null,
  payMonthOverviews: [],
  payAmountSummaryData: {
    numberOfNewEntries: 0,
    totalPayAmountInNewEntries: 0,
    numberOfChangedEntries: 0,
    totalPayAmountInChangedEntries: 0,
  },
  reportDate: null,
  isDraft: false,
  reportingBlockedByExpiringFok: false,
  loadingPayReportData: ThunkRequestStatus.Pending,
  savingDraftData: ThunkRequestStatus.Pending,
  loadingPayMonthOverviews: ThunkRequestStatus.Pending,
  loadingDeleteDrafts: ThunkRequestStatus.Fulfilled,
  loadingReportPayroll: null,
  tableUISettings: {
    showAlertIconLegend: false,
    showInactiveIconLegend: false,
  },
  reportButtonDisabled: false,
  isAnyReportProcessing: false,
  payrollInputPanelState: { isOpen: true, status: PanelStatus.Active },
  reviewAndConfirmPayReportPanelState: { isOpen: false, status: PanelStatus.Inactive },
  receiptPanelState: { isOpen: false, status: PanelStatus.Inactive },
};

const payrollSlice = createSlice({
  name: 'payroll',
  initialState: initialPayrollState,
  reducers: {
    resetPayrollReportingStates(state) {
      state.payrollInputPanelState = initialPayrollState.payrollInputPanelState;
      state.reviewAndConfirmPayReportPanelState = initialPayrollState.reviewAndConfirmPayReportPanelState;
      state.receiptPanelState = initialPayrollState.receiptPanelState;
      state.reviewAndConfirmChecked = initialPayrollState.reviewAndConfirmChecked;
      state.loadingReportPayroll = initialPayrollState.loadingReportPayroll;
      state.loadingPayReportData = initialPayrollState.loadingPayReportData;
      state.payAmountSummaryData = initialPayrollState.payAmountSummaryData;
      state.reportDate = initialPayrollState.reportDate;
      state.tableUISettings = initialPayrollState.tableUISettings;
      state.reportingBlockedByExpiringFok = false;
      state.payReportingTableData = [];
      state.reportButtonDisabled = false;
      state.draftUpdatedAt = undefined;
      state.isDraft = false;
      state.inputPayData = {};
    },
    goToPayrollInput(state) {
      state.payrollInputPanelState = activePanelState();
      state.reviewAndConfirmPayReportPanelState = resetPanelState();
      state.receiptPanelState = resetPanelState();
    },
    goToReviewAndConfirm(state) {
      state.payrollInputPanelState = completePanelState();
      state.reviewAndConfirmPayReportPanelState = activePanelState();
    },
    goToReceipt(state) {
      state.reviewAndConfirmPayReportPanelState = completePanelState();
      state.receiptPanelState = activePanelState();
    },
    restartReporting(state) {
      state.payrollInputPanelState = activePanelState();
      state.reviewAndConfirmPayReportPanelState = resetPanelState();
      state.receiptPanelState = resetPanelState();
      state.reviewAndConfirmChecked = initialPayrollState.reviewAndConfirmChecked;
      state.loadingReportPayroll = initialPayrollState.loadingReportPayroll;
      state.reportDate = initialPayrollState.reportDate;
      state.reportButtonDisabled = false;
      state.payReportingTableData = [];
      state.inputPayData = {};
    },
    setReportButtonState(state, action: PayloadAction<boolean>) {
      state.reportButtonDisabled = action.payload;
    },
    setEmploymentPay(
      state,
      action: PayloadAction<{
        policyNumber: string;
        payAmount: number | null;
        attributablePay?: AttributablePay;
      }>
    ) {
      const policyNumber = action.payload.policyNumber;
      state.inputPayData[policyNumber].payAmount = action.payload.payAmount;

      if (action.payload.attributablePay) {
        state.inputPayData[policyNumber].attributablePay = action.payload.attributablePay;
      }
    },
    setPayInputError(state, action: PayloadAction<{ policyNumber: string; error: boolean }>) {
      state.inputPayData[action.payload.policyNumber].hasError = action.payload.error;
    },
    setPayInputErrors(state, action: PayloadAction<{ policyNumber: string; error: boolean }[]>) {
      action.payload.forEach(emp => {
        state.inputPayData[emp.policyNumber].hasError = emp.error;
      });
    },
    setPayAmountSummaryData(state, action: PayloadAction<ManualPayrollReportSummary>) {
      state.payAmountSummaryData = action.payload;
    },
    setReviewAndConfirmChecked(state, action: PayloadAction<SetReviewAndConfirmCheckboxPayload>) {
      state.reviewAndConfirmChecked = action.payload.checked;
    },
    setSelectedReportingPeriod(state, action: PayloadAction<SetSelectedReportingPeriodPayload>) {
      state.selectedPayMonthOverview = action.payload.selectedPayMonthOverview;
    },
    resetLoadingDeleteDrafts(state) {
      state.loadingDeleteDrafts = ThunkRequestStatus.Fulfilled;
    },
  },
  extraReducers: builder => {
    // Get pay report data for period
    builder.addCase(getPayReportData.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingPayReportData = ThunkRequestStatus.Fulfilled;
        state.payReportingTableData = action.payload.payReportRows as PayReportingTableRowViewModel[];
        state.draftUpdatedAt = action.payload.draftUpdatedAt;
        state.inputPayData = {};
        state.isDraft = false;
        for (const payDraftDataItem of state.payReportingTableData) {
          // Payroll input form data
          let attributablePay = undefined;
          if (payDraftDataItem.attributablePayDraft && payDraftDataItem.attributablePayDraft.length > 0) {
            attributablePay = payDraftDataItem.attributablePayDraft;
          } else if (payDraftDataItem.attributablePayReported && payDraftDataItem.attributablePayReported.length > 0) {
            attributablePay = payDraftDataItem.attributablePayReported;
          }
          const payAmount = payDraftDataItem.payAmountDraft ?? payDraftDataItem.payAmountReported;

          // Draft
          if (
            !state.isDraft &&
            (payDraftDataItem.payAmountDraft !== null || payDraftDataItem.attributablePayDraft !== null)
          ) {
            state.isDraft = true;
          }

          state.inputPayData[payDraftDataItem.policyNumber] = {
            policyNumber: payDraftDataItem.policyNumber,
            employeeId: payDraftDataItem.employee.employeeId,
            firstName: payDraftDataItem.employee.firstName,
            lastName: payDraftDataItem.employee.lastName,
            payAmount,
            payAmountReported: payDraftDataItem.payAmountReported,
            attributablePay,
            attributablePayReported: payDraftDataItem.attributablePayReported,
          };

          if (payDraftDataItem.activeMarkup?.fokIsExpiring) {
            state.inputPayData[payDraftDataItem.policyNumber].hasError = true;
            state.reportingBlockedByExpiringFok = true;
          }

          // Table UI config
          if (!state.tableUISettings.showAlertIconLegend && payDraftDataItem.hasFokParameterChoices) {
            state.tableUISettings.showAlertIconLegend = true;
          }
          if (!state.tableUISettings.showInactiveIconLegend && payDraftDataItem.isInactive) {
            state.tableUISettings.showInactiveIconLegend = true;
          }
        }
      }
    });
    builder.addCase(getPayReportData.pending, state => {
      state.loadingPayReportData = ThunkRequestStatus.Pending;
    });
    builder.addCase(getPayReportData.rejected, state => {
      state.loadingPayReportData = ThunkRequestStatus.Rejected;
      state.payReportingTableData = [];
      state.draftUpdatedAt = undefined;
    });

    // Get pay report data for period
    builder.addCase(getPayReportDataOld.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingPayReportData = ThunkRequestStatus.Fulfilled;
        state.payReportingTableDataOld = action.payload.payReportRows as PayReportingTableRowViewModelOld[];
        state.draftUpdatedAt = action.payload.draftUpdatedAt;
        state.inputPayData = {};
        state.isDraft = false;
        for (const payDraftDataItem of state.payReportingTableDataOld) {
          // Payroll input form data
          let attributablePay = undefined;
          if (payDraftDataItem.attributablePayDraft && payDraftDataItem.attributablePayDraft.length > 0) {
            attributablePay = payDraftDataItem.attributablePayDraft;
          } else if (payDraftDataItem.attributablePayReported && payDraftDataItem.attributablePayReported.length > 0) {
            attributablePay = payDraftDataItem.attributablePayReported;
          }
          const payAmount = payDraftDataItem.payAmountDraft ?? payDraftDataItem.payAmountReported;

          // Draft
          if (
            !state.isDraft &&
            (payDraftDataItem.payAmountDraft !== null || payDraftDataItem.attributablePayDraft !== null)
          ) {
            state.isDraft = true;
          }

          state.inputPayData[payDraftDataItem.policyNumber] = {
            policyNumber: payDraftDataItem.policyNumber,
            employeeId: payDraftDataItem.employee.employeeId,
            firstName: payDraftDataItem.employee.firstName,
            lastName: payDraftDataItem.employee.lastName,
            payAmount,
            payAmountReported: payDraftDataItem.payAmountReported,
            attributablePay,
            attributablePayReported: payDraftDataItem.attributablePayReported,
          };

          if (payDraftDataItem.activeMarkup?.fokIsExpiring) {
            state.inputPayData[payDraftDataItem.policyNumber].hasError = true;
            state.reportingBlockedByExpiringFok = true;
          }

          // Table UI config
          if (!state.tableUISettings.showAlertIconLegend && payDraftDataItem.hasFokParameterChoices) {
            state.tableUISettings.showAlertIconLegend = true;
          }
          if (!state.tableUISettings.showInactiveIconLegend && payDraftDataItem.isInactive) {
            state.tableUISettings.showInactiveIconLegend = true;
          }
        }
      }
    });
    builder.addCase(getPayReportDataOld.pending, state => {
      state.loadingPayReportData = ThunkRequestStatus.Pending;
    });
    builder.addCase(getPayReportDataOld.rejected, state => {
      state.loadingPayReportData = ThunkRequestStatus.Rejected;
      state.payReportingTableDataOld = [];
      state.draftUpdatedAt = undefined;
    });

    // Save draft data
    builder.addCase(setEmploymentPayDraft.fulfilled, (state, action) => {
      if (action.payload) {
        state.savingDraftData = ThunkRequestStatus.Fulfilled;

        if (action.payload.timeStamp) {
          state.draftUpdatedAt = action.payload.timeStamp;
        }
      }
    });
    builder.addCase(setEmploymentPayDraft.pending, state => {
      state.savingDraftData = ThunkRequestStatus.Pending;
    });
    builder.addCase(setEmploymentPayDraft.rejected, state => {
      state.savingDraftData = ThunkRequestStatus.Rejected;
    });
    // Get pay months and reporting periods
    builder.addCase(getPayMonthOverviews.fulfilled, (state, action) => {
      if (action.payload) {
        state.payMonthOverviews = action.payload.payMonthOverviews;
        state.isAnyReportProcessing = action.payload.isAnyReportProcessing;
        state.loadingPayMonthOverviews = ThunkRequestStatus.Fulfilled;
      }
    });
    builder.addCase(getPayMonthOverviews.pending, state => {
      state.loadingPayMonthOverviews = ThunkRequestStatus.Pending;
    });
    builder.addCase(getPayMonthOverviews.rejected, state => {
      state.loadingPayMonthOverviews = ThunkRequestStatus.Rejected;
    });

    // Report payroll
    builder.addCase(reportPayroll.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingReportPayroll = ThunkRequestStatus.Fulfilled;
        state.reportDate = action.payload.timeStamp;
      }
    });
    builder.addCase(reportPayroll.pending, state => {
      state.loadingReportPayroll = ThunkRequestStatus.Pending;
    });
    builder.addCase(reportPayroll.rejected, state => {
      state.loadingReportPayroll = ThunkRequestStatus.Rejected;
    });

    // Delete Draft
    builder.addCase(deleteDrafts.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingDeleteDrafts = ThunkRequestStatus.Fulfilled;
      }
    });
    builder.addCase(deleteDrafts.pending, state => {
      state.loadingDeleteDrafts = ThunkRequestStatus.Pending;
    });
    builder.addCase(deleteDrafts.rejected, state => {
      state.loadingDeleteDrafts = ThunkRequestStatus.Rejected;
    });
  },
});

export const {
  resetPayrollReportingStates,
  goToPayrollInput,
  goToReviewAndConfirm,
  goToReceipt,
  setReportButtonState,
  setPayAmountSummaryData,
  setReviewAndConfirmChecked,
  setSelectedReportingPeriod,
  setEmploymentPay,
  setPayInputError,
  setPayInputErrors,
  resetLoadingDeleteDrafts,
  restartReporting,
} = payrollSlice.actions;

export const payrollReducer = payrollSlice.reducer;
